import { useEffect, useState } from "react";
import type { UnformattedStructuredDataProduct } from "@made-people/grease-structured-data";
import type { TrustpilotDisplayData } from "@made-people/grease-trustpilot";
import type { BffClient } from "@made-people/gears-bff-client";
import { fetchTrustpilotProductReviews } from "../trustpilot/fetch-trustpilot-product-reviews";
import { getTrustpilotSkusForProductData } from "../trustpilot/get-trustpilot-skus-for-product-data";
import { TRUSTPILOT_CONSTANTS } from "../../components/features/trustpilot/trustpilot-constants";

const MM_BRAND_FALLBACK = "Miss Mary";

export function withMmBrandFallback(
  unformatted: UnformattedStructuredDataProduct,
): UnformattedStructuredDataProduct;
export function withMmBrandFallback(
  unformatted: UnformattedStructuredDataProduct | undefined,
): UnformattedStructuredDataProduct | undefined;
export function withMmBrandFallback(
  unformatted: UnformattedStructuredDataProduct | undefined,
): UnformattedStructuredDataProduct | undefined {
  if (!unformatted) {
    return unformatted;
  }
  if (
    typeof unformatted.brandName === "string" &&
    unformatted.brandName.length > 0
  ) {
    return unformatted;
  }
  return { ...unformatted, brandName: MM_BRAND_FALLBACK };
}

export function useMmPdpReviewsForSchema(
  bffClient: BffClient,
  productData: unknown,
  language: string,
  enabled: boolean,
): TrustpilotDisplayData | null {
  const [reviews, setReviews] = useState<TrustpilotDisplayData | null>(null);
  const skuString = enabled
    ? getTrustpilotSkusForProductData(productData).join(",")
    : "";

  useEffect(() => {
    setReviews(null);

    if (!skuString) {
      return;
    }

    let cancelled = false;
    fetchTrustpilotProductReviews(bffClient, {
      productSku: skuString,
      language,
      perPage: TRUSTPILOT_CONSTANTS.REVIEWS_PER_PAGE_INITIAL,
      page: 1,
    }).then((data) => {
      if (!cancelled) {
        setReviews(data);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [bffClient, skuString, language]);

  return reviews;
}

export function trustpilotReviewsToSchemaFields(
  data: TrustpilotDisplayData | null,
): Pick<UnformattedStructuredDataProduct, "aggregateRating" | "reviews"> {
  if (!data || data.totalReviews <= 0) {
    return {};
  }

  return {
    aggregateRating: {
      ratingValue: data.averageRating,
      bestRating: 5,
      worstRating: 1,
      reviewCount: data.totalReviews,
      ratingCount: data.totalReviews,
    },
    reviews: data.reviews.map((r) => ({
      reviewBody: r.text,
      name: r.title,
      datePublished: r.date,
      reviewRating: {
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        name: r.author,
        type: "Person",
      },
    })),
  };
}
